import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FacetBlock, RangeBlock } from '@/features/shared/components/Facet';
import { arr } from '@/features/shared/lib/utils';
import noUiSlider from 'nouislider';
import '@/styles/nouislider.min.css';

export default function JournalsFilterSidebar({
  facet,
  filters,
  setFilters,
  pendingFilters,
  setPendingFilters,
  conjunctions,
  setConjunctions,
  sort,
  query,
  setQuery,
  doSearch,
  syncUrl,
  fetchList,
  cleanFilters,
  setPopup,
  setGraphExpanded,
}) {
  // Debounce timer for search input
  const typingTimerRef = useRef(null);
  useEffect(() => () => { if (typingTimerRef.current) clearTimeout(typingTimerRef.current); }, []);
  const DEBOUNCE_MS = 500;
  const runSearchNow = () => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (typeof doSearch === 'function') doSearch();
  };
  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    if (typeof setQuery === 'function') setQuery(val);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      runSearchNow();
    }, DEBOUNCE_MS);
  };

  // JCR JIF Range 탭 상태
  const [jifTab, setJifTab] = useState('range'); // 'range' | 'individual'
  const [jifRange, setJifRange] = useState([null, null]); // [min, max]
  const [jifRangePending, setJifRangePending] = useState([null, null]);
  const jifSliderRef = useRef(null);
  const jifSliderInstanceRef = useRef(null);
  const jifMinInputRef = useRef(null);
  const jifMaxInputRef = useRef(null);
  const jifSvgPathRef = useRef(null);

  // jcr_jif_range 패싯 데이터
  const jifRangeFacet = useMemo(() => {
    const jifData = facet?.jcr_jif_range;
    if (!jifData || jifData.type !== 'range' || !jifData.result) return null;
    const arrayData = Array.isArray(jifData.result.array) ? jifData.result.array : [];
    return {
      min: jifData.result.min ?? 0,
      max: jifData.result.max ?? 1000,
      array: arrayData.map(item => ({
        value: Number(item.value || 0),
        count: Number(item.count || 0)
      })).sort((a, b) => a.value - b.value)
    };
  }, [facet?.jcr_jif_range]);

  // SVG 차트 경로 생성
  const generateJifSVGPath = useMemo(() => {
    if (!jifRangeFacet || !jifRangeFacet.array.length) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';

    const width = 400;
    const height = 80;
    const padding = 20;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const maxCount = Math.max(...jifRangeFacet.array.map(item => item.count));
    const minCount = Math.min(...jifRangeFacet.array.map(item => item.count));
    const countRange = maxCount - minCount || 1;

    // value 범위에 맞춰 포인트 생성
    const points = jifRangeFacet.array.map(item => {
      const normalizedValue = (item.value - jifRangeFacet.min) / (jifRangeFacet.max - jifRangeFacet.min || 1);
      const x = padding + normalizedValue * chartWidth;
      const normalizedCount = countRange > 0 ? (item.count - minCount) / countRange : 0;
      const y = height - padding - normalizedCount * chartHeight;
      return { x, y, value: item.value, count: item.count };
    });

    if (points.length === 0) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';

    // 스무스 커브 경로 생성
    let path = `M${points[0].x},${points[0].y}`;

    if (points.length === 1) {
      path += ` L${points[0].x},${height} L${points[0].x},${height} Z`;
    } else {
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1] || curr;

        const dx = (next.x - prev.x) * 0.2;
        const cp1x = prev.x + dx;
        const cp1y = prev.y;
        const cp2x = curr.x - dx;
        const cp2y = curr.y;

        path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
      }

      path += ` L${points[points.length - 1].x},${height} L${points[0].x},${height} Z`;
    }

    return path;
  }, [jifRangeFacet]);

  // 슬라이더 초기화
  useEffect(() => {
    if (jifTab !== 'range' || !jifSliderRef.current || !jifRangeFacet) return;

    const slider = jifSliderRef.current;

    // 기존 슬라이더 제거
    if (jifSliderInstanceRef.current) {
      jifSliderInstanceRef.current.destroy();
    }

    // 새 슬라이더 생성
    const instance = noUiSlider.create(slider, {
      start: [jifRangeFacet.min, jifRangeFacet.max],
      connect: true,
      range: {
        min: jifRangeFacet.min,
        max: jifRangeFacet.max
      },
      step: (jifRangeFacet.max - jifRangeFacet.min) / 1000,
      format: {
        to: (value) => Number(value).toFixed(1),
        from: (value) => Number(value)
      }
    });

    jifSliderInstanceRef.current = instance;

    // 슬라이더 값 변경 시
    instance.on('update', (values) => {
      const [min, max] = values.map(v => Number(v));
      setJifRangePending([min, max]);
      if (jifMinInputRef.current) jifMinInputRef.current.value = min.toFixed(1);
      if (jifMaxInputRef.current) jifMaxInputRef.current.value = max.toFixed(1);
    });

    // 입력 필드 값 변경 시
    const handleMinInput = (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= jifRangeFacet.min && value <= jifRangeFacet.max) {
        instance.set([value, null]);
      }
    };

    const handleMaxInput = (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= jifRangeFacet.min && value <= jifRangeFacet.max) {
        instance.set([null, value]);
      }
    };

    if (jifMinInputRef.current) {
      jifMinInputRef.current.addEventListener('change', handleMinInput);
    }
    if (jifMaxInputRef.current) {
      jifMaxInputRef.current.addEventListener('change', handleMaxInput);
    }

    return () => {
      if (jifMinInputRef.current) {
        jifMinInputRef.current.removeEventListener('change', handleMinInput);
      }
      if (jifMaxInputRef.current) {
        jifMaxInputRef.current.removeEventListener('change', handleMaxInput);
      }
      if (instance) {
        instance.destroy();
      }
    };
  }, [jifTab, jifRangeFacet]);

  // SVG 경로 업데이트
  useEffect(() => {
    if (jifSvgPathRef.current && generateJifSVGPath) {
      jifSvgPathRef.current.setAttribute('d', generateJifSVGPath);
    }
  }, [generateJifSVGPath]);

  // Range 탭에서 Refine 클릭 시
  const handleJifRangeRefine = () => {
    const [min, max] = jifRangePending;
    if (min === null || max === null || min > max) return;

    const next = { ...filters };
    const minParam = `min_jcr_jif_range`;
    const maxParam = `max_jcr_jif_range`;

    if (jifRangeFacet && min === jifRangeFacet.min) {
      delete next[minParam];
    } else {
      next[minParam] = min;
    }

    if (jifRangeFacet && max === jifRangeFacet.max) {
      delete next[maxParam];
    } else {
      next[maxParam] = max;
    }

    setFilters(next);
    setJifRange([min, max]);
    if (setGraphExpanded) setGraphExpanded(false);

    const extra = {};
    Object.entries(next).forEach(([k, v]) => {
      if (k.startsWith('min_') || k.startsWith('max_')) {
        extra[k] = v;
      } else if (Array.isArray(v) && v.length) {
        extra[`${k}[]`] = v;
      }
    });
    Object.entries(conjunctions).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();

    syncUrl({ page: 1, filters: next, sort, conjunctions });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) { }
    fetchList(1, { extra });
  };

  // Individual 탭에서 jcr_jif_range 필터 관리
  const handleJifIndividualToggle = (value) => {
    setPendingFilters(prev => {
      const prevArr = Array.isArray(prev.jcr_jif_range) ? prev.jcr_jif_range : [];
      const exists = prevArr.includes(value);
      const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
      return { ...prev, jcr_jif_range: nextArr };
    });
  };

  const handleJifIndividualRefine = () => {
    const next = cleanFilters(pendingFilters || {});
    setFilters(next);
    setPendingFilters(next);
    if (setGraphExpanded) setGraphExpanded(false);
    const extra = {};
    Object.entries(next).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) {
        extra[`${k}[]`] = vals;
      }
    });
    Object.entries(conjunctions).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();
    syncUrl({ page: 1, filters: next, sort, conjunctions });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) { }
    fetchList(1, { extra });
  };

  // ----- _percent facet (타입2: 퍼센트 범위 슬라이더, 입력 없음, % 라벨만) -----
  const percentFacets = useMemo(() => {
    if (!facet || typeof facet !== 'object') return [];
    return Object.entries(facet).filter(([key]) => key.includes('_percent'));
  }, [facet]);

  const [percentRangePendingState, setPercentRangePendingState] = useState({});
  const percentSliderRefs = useRef({});
  const percentSliderInstanceRefs = useRef({});

  const PERCENT_LABELS = [0, 20, 40, 60, 80, 100];
  const getPercentRangePending = (facetKey) => percentRangePendingState[facetKey] ?? [0, 100];
  const setPercentRangePending = (facetKey, range) => setPercentRangePendingState((prev) => ({ ...prev, [facetKey]: range }));

  const buildPercentChartPath = (result) => {
    if (!Array.isArray(result) || !result.length) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';
    const width = 400;
    const height = 80;
    const padding = 0;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;
    const parseCenter = (id) => {
      if (!id || typeof id !== 'string') return 50;
      const parts = id.split('-').map(Number).filter((n) => !isNaN(n));
      if (parts.length >= 2) return (parts[0] + parts[1]) / 2;
      return parts[0] ?? 50;
    };
    const points = result
      .map((item) => ({ x: parseCenter(item.id), y: Number(item.count) || 0 }))
      .sort((a, b) => a.x - b.x);
    if (!points.length) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';
    const maxCount = Math.max(...points.map((p) => p.y));
    const minCount = Math.min(...points.map((p) => p.y));
    const countRange = maxCount - minCount || 1;
    let path = '';
    points.forEach((p, i) => {
      const nx = padding + (p.x / 100) * chartWidth;
      const ny = height - padding - ((p.y - minCount) / countRange) * chartHeight;
      if (i === 0) path = `M${nx},${ny}`;
      else {
        const prev = points[i - 1];
        const px = padding + (prev.x / 100) * chartWidth;
        const py = height - padding - ((prev.y - minCount) / countRange) * chartHeight;
        const dx = (nx - px) * 0.2;
        path += ` C${px + dx},${py} ${nx - dx},${ny} ${nx},${ny}`;
      }
    });
    const lastX = padding + (points[points.length - 1].x / 100) * chartWidth;
    const firstX = padding + (points[0].x / 100) * chartWidth;
    path += ` L${lastX},${height} L${firstX},${height} Z`;
    return path;
  };

  const runRefineWithExtra = (nextFilters) => {
    const extra = {};
    Object.entries(nextFilters).forEach(([k, v]) => {
      if (k.startsWith('min_') || k.startsWith('max_')) {
        extra[k] = v;
      } else if (Array.isArray(v) && v.length) {
        extra[`${k}[]`] = v;
      }
    });
    Object.entries(conjunctions).forEach(([k, conj]) => {
      if (conj === 'and') extra[`${k}_conjunction`] = 'and';
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();
    syncUrl({ page: 1, filters: nextFilters, sort, conjunctions });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) { }
    fetchList(1, { extra });
  };

  const handlePercentRangeRefine = (facetKey) => {
    const [min, max] = getPercentRangePending(facetKey);
    if (min == null || max == null || min > max) return;
    const rangeValue = `${min}-${max}`;
    const next = { ...filters };
    delete next[facetKey];
    delete next[`min_${facetKey}`];
    delete next[`max_${facetKey}`];
    next[facetKey] = [rangeValue];
    setFilters(next);
    setPendingFilters((prev) => ({ ...prev, [facetKey]: [rangeValue] }));
    if (setGraphExpanded) setGraphExpanded(false);
    runRefineWithExtra(next);
  };

  const isRangeOnlyValue = (arr, bucketIds) =>
    Array.isArray(arr) &&
    arr.length === 1 &&
    Array.isArray(bucketIds) &&
    bucketIds.length > 0 &&
    !bucketIds.includes(arr[0]);

  const handlePercentIndividualToggle = (facetKey, id, bucketIds = []) => {
    setPendingFilters((prev) => {
      const arr = Array.isArray(prev[facetKey]) ? prev[facetKey] : [];
      const exists = arr.includes(id);
      const wasRangeOnly = isRangeOnlyValue(arr, bucketIds);
      const nextArr = wasRangeOnly
        ? (exists ? [] : [id])
        : exists
          ? arr.filter((v) => v !== id)
          : [...arr, id];
      return { ...prev, [facetKey]: nextArr };
    });
  };

  const handlePercentIndividualRefine = (facetKey, result) => {
    const selected = Array.isArray(pendingFilters[facetKey]) ? pendingFilters[facetKey] : [];
    const bucketIds = (result || []).map((r) => r.id);
    const onlyIndividualIds = selected.filter((id) => bucketIds.includes(id));
    const next = { ...filters };
    delete next[facetKey];
    delete next[`min_${facetKey}`];
    delete next[`max_${facetKey}`];
    next[facetKey] = onlyIndividualIds.length ? onlyIndividualIds : [];
    if (onlyIndividualIds.length === 0) delete next[facetKey];
    setFilters(next);
    setPendingFilters((prev) => ({ ...prev, [facetKey]: next[facetKey] || [] }));
    if (setGraphExpanded) setGraphExpanded(false);
    runRefineWithExtra(next);
  };

  useEffect(() => {
    percentFacets.forEach(([facetKey, value]) => {
      const result = value?.result;
      if (!Array.isArray(result) || !result.length) return;
      const sliderEl = percentSliderRefs.current[facetKey];
      if (!sliderEl) return;

      if (percentSliderInstanceRefs.current[facetKey]) {
        percentSliderInstanceRefs.current[facetKey].destroy();
        percentSliderInstanceRefs.current[facetKey] = null;
      }

      // Range 모드일 때만(단일 범위 값 1개) 슬라이더에 반영. Individual 모드(여러 개)면 0~100 표시
      let startMin = 0;
      let startMax = 100;
      const rangeVal = filters[facetKey];
      if (Array.isArray(rangeVal) && rangeVal.length === 1 && typeof rangeVal[0] === 'string') {
        const parts = rangeVal[0].split('-').map(Number);
        if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          startMin = parts[0];
          startMax = parts[1];
        }
      }

      const instance = noUiSlider.create(sliderEl, {
        start: [startMin, startMax],
        connect: true,
        range: { min: 0, max: 100 },
        step: 1,
        format: { to: (v) => Math.round(v), from: Number },
      });

      percentSliderInstanceRefs.current[facetKey] = instance;
      setPercentRangePending(facetKey, [startMin, startMax]);

      instance.on('update', (values) => {
        const [min, max] = values.map(Number);
        setPercentRangePending(facetKey, [min, max]);
      });
    });

    return () => {
      percentFacets.forEach(([facetKey]) => {
        if (percentSliderInstanceRefs.current[facetKey]) {
          percentSliderInstanceRefs.current[facetKey].destroy();
          percentSliderInstanceRefs.current[facetKey] = null;
        }
      });
    };
  }, [percentFacets, filters]);

  const PERCENT_VIEWBOX_W = 400;
const PERCENT_PAD = 20;
const PAD_PCT = (PERCENT_PAD / PERCENT_VIEWBOX_W) * 100; // 5

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const leftFromPercent = (v) => {
  const t = clamp01((Number(v) || 0) / 100);
  return `${PAD_PCT + t * (100 - PAD_PCT * 2)}%`;
};

  return (
    <div className="lg:w-[310px] lg:mt-[45px]">
      <div className="filter fixed top-[100%] right-0 z-[101] w-full h-full transition-top duration-300 overflow-y-auto px-6 py-[30px] bg-[#fff] overflow-y-auto lg:rounded-xl lg:static">
        <div className="filter-top flex items-center gap-[10px] relative lg:justify-between">
          <h3 className="text-[18px] font-bold">Journal Search</h3>
          <button
            className="btn-resetAll text-[13px] text-[#6C7687] font-semibold cursor-pointer"
            onClick={() => {
              setFilters({});
              setPendingFilters({});
              setConjunctions({});
              const extra = {};
              if (sort?.order_by) extra.order_by = sort.order_by;
              if (sort?.order) extra.order = sort.order;
              if (String(query || '').trim()) extra.sub_ks = String(query).trim();
              syncUrl({ page: 1, filters: {}, sort, conjunctions: {} });
              fetchList(1, { extra });
            }}
          >
            Reset all
          </button>
          <button className="filter-close absolute top-0 right-0 w-[25px] h-[25px] cursor-pointer min-[800px]:hidden">
            <img src={`${rmVars.themeUrl}images/clear-icon.png`} className="w-[18px]" alt="close" />
          </button>
        </div>

        <div className="filter-search relative mt-[20px]">
          <input
            type="text"
            placeholder="Search within result..."
            value={query}
            onChange={handleSearchInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearchNow();
            }}
            className="w-full h-[46px] px-4 pr-[46px] border border-[#E4E8F1] outline-none rounded-lg text-[13px] text-[#333] placeholder:text-[#B4BCCC] hover:border-[#8D9AAF] focus:border-[#8D9AAF] disabled:border-[#E4E8F1] disabled:bg-[#F3F5F9] disabled:text-[#B4BCCC] disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={runSearchNow}
            className="absolute top-1/2 right-[18px] -translate-y-1/2 cursor-pointer"
          >
            <img src={`${rmVars.themeUrl}images/search-ico-8D9AAF.png`} className="w-[18px]" alt="search icon" />
          </button>
        </div>

        <div className="filter-cont mt-10">
          <div className="filter-item-wrap">
            {Object.entries(facet)
              .filter(([key]) => key !== 'jcr_jif_range' && !key.includes('_percent')) // jcr_jif_range, _percent는 별도 섹션에서 처리
              .sort((a, b) => (a[0] === 'registered_db_name' ? -1 : (b[0] === 'registered_db_name' ? 1 : 0)))
              .map(([key, value]) => {
                // type이 "range"인 경우 RangeBlock 사용
                if (value?.type === 'range') {
                  const title = value?.title || key
                    .split('_')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                    .join(' ');

                  const handleRangeRefine = (facetKey, min, max) => {
                    const next = { ...filters };
                    const minParam = `min_${facetKey}`;
                    const maxParam = `max_${facetKey}`;
                    const rangeData = value?.result || {};
                    const defaultMin = rangeData.min ?? 0;
                    const defaultMax = rangeData.max ?? 1000;

                    // 각 값이 기본값과 동일하면 파라미터에서 제외
                    if (min !== undefined && min === defaultMin) {
                      delete next[minParam];
                    } else if (min !== undefined) {
                      next[minParam] = min;
                    }

                    if (max !== undefined && max === defaultMax) {
                      delete next[maxParam];
                    } else if (max !== undefined) {
                      next[maxParam] = max;
                    }

                    setFilters(next);
                    // 그래프 닫기
                    if (setGraphExpanded) setGraphExpanded(false);
                    const extra = {};
                    Object.entries(next).forEach(([k, v]) => {
                      if (k.startsWith('min_') || k.startsWith('max_')) {
                        extra[k] = v;
                      } else if (Array.isArray(v) && v.length) {
                        extra[`${k}[]`] = v;
                      }
                    });
                    // conjunction 파라미터 추가
                    Object.entries(conjunctions).forEach(([k, conj]) => {
                      if (conj === 'and') {
                        extra[`${k}_conjunction`] = 'and';
                      }
                    });
                    if (sort?.order_by) extra.order_by = sort.order_by;
                    if (sort?.order) extra.order = sort.order;
                    if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                    syncUrl({ page: 1, filters: next, sort, conjunctions });
                    try {
                      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                      }
                    } catch (e) {
                    }
                    fetchList(1, { extra });
                  };

                  return (
                    <RangeBlock
                      key={key}
                      title={title}
                      facetKey={key}
                      facetData={value}
                      filters={filters}
                      onRefine={handleRangeRefine}
                    />
                  );
                }

                // 일반 facet은 FacetBlock 사용
                const items = value?.result ? value : arr(value);
                if (Array.isArray(items) && !items.length) return null;
                if (items && typeof items === 'object' && !Array.isArray(items) && Array.isArray(items.result) && !items.result.length) return null;

                // key를 사람이 읽기 좋은 형태로 변환
                const title = value?.title || key
                  .split('_')
                  .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                  .join(' ');

                // 특정 facet만 옵션 활성화
                const isRegisteredDb = key === 'registered_db_name';

                // conjunction 필드 확인 (dept 등)
                const hasConjunction = value?.is_conjunction;
                const currentConjunction = hasConjunction ? (conjunctions[key] || null) : null;

                return (
                  <FacetBlock
                    key={key}
                    title={title}
                    items={items}
                    showAll={isRegisteredDb}
                    andQuery={isRegisteredDb}
                    facetKey={key}
                    selected={pendingFilters[key] || []}
                    conjunction={hasConjunction ? 'and' : null}
                    conjunctionValue={currentConjunction}
                    onConjunctionChange={(facetKey, conjunctionValue) => {
                      setConjunctions(prev => ({
                        ...prev,
                        [facetKey]: conjunctionValue
                      }));
                    }}
                    onToggle={(facetKey, value) => {
                      setPendingFilters(prev => {
                        const prevArr = Array.isArray(prev[facetKey]) ? prev[facetKey] : [];
                        const exists = prevArr.includes(value);
                        const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
                        return { ...prev, [facetKey]: nextArr };
                      });
                    }}
                    onRefine={() => {
                      const next = cleanFilters(pendingFilters || {});
                      setFilters(next);
                      setPendingFilters(next);
                      // 그래프 닫기
                      if (setGraphExpanded) setGraphExpanded(false);
                      const extra = {};
                      Object.entries(next).forEach(([k, vals]) => {
                        if (Array.isArray(vals) && vals.length) extra[`${k}[]`] = vals;
                      });
                      // conjunction 파라미터 추가
                      Object.entries(conjunctions).forEach(([k, conj]) => {
                        if (conj === 'and') {
                          extra[`${k}_conjunction`] = 'and';
                        }
                      });
                      if (sort?.order_by) extra.order_by = sort.order_by;
                      if (sort?.order) extra.order = sort.order;
                      if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                      syncUrl({ page: 1, filters: next, sort, conjunctions });
                      try {
                        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                        }
                      } catch (e) {
                      }
                      fetchList(1, { extra });
                    }}
                    onMore={(params) => {
                      // FacetBlock은 facetKey로 키를 넘김 → Popup은 key를 사용함
                      setPopup({ open: true, key: params?.facetKey || params?.key || '', ...params });
                    }}
                  />
                );
              })}

            {/* _percent facet: Range만 — SVG와 동일 너비 슬라이더, 슬라이더 아래 양끝에 현재 값(%) 표시 */}
            {percentFacets.map(([facetKey, value]) => {
              const title = value?.title || facetKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
              const result = Array.isArray(value?.result) ? value.result : [];
              const chartPath = buildPercentChartPath(result);
              const [pendingMin, pendingMax] = getPercentRangePending(facetKey);
              return (
                <div key={facetKey} className="filter-item mb-9">
                  <h4 className="mb-3 text-[14px] font-bold">{title}</h4>
                  <div className="relative w-full min-w-0">
  <div className="w-full min-w-0" style={{ aspectRatio: '400/80' }}>
    <svg viewBox="0 0 400 80" className="block w-full h-full align-top" preserveAspectRatio="none">
      <path d={chartPath} fill="#4B4DFF1A" stroke="none" />
    </svg>
  </div>

  {/* ✅ SVG padding(5%)만큼 안쪽으로 줄인 트랙 */}
  <div
    className="mt-2"
    style={{ marginLeft: `${PAD_PCT}%`, width: `${100 - PAD_PCT * 2}%` }}
  >
    <div
      ref={(el) => { percentSliderRefs.current[facetKey] = el; }}
      className="percent-range-slider-wrap relative z-10 w-full"
    />
  </div>

  {/* ✅ 현재 값 라벨도 같은 스케일로 */}
  <div className="relative mt-2 h-5 w-full flex items-center">
    <span
      className="absolute text-[11px] text-[#376BDB] font-semibold -translate-x-1/2"
      style={{ left: leftFromPercent(pendingMin) }}
    >
      {Math.round(Number(pendingMin) || 0)}%
    </span>
    <span
      className="absolute text-[11px] text-[#376BDB] font-semibold -translate-x-1/2"
      style={{ left: leftFromPercent(pendingMax) }}
    >
      {Math.round(Number(pendingMax) || 100)}%
    </span>
  </div>

  {/* ✅ 0/20/.. tick도 같은 스케일로 */}
  <div className="relative mt-1 h-4 w-full">
    {PERCENT_LABELS.map((pct) => (
      <span
        key={pct}
        className="absolute text-[11px] text-[#6C7687] -translate-x-1/2"
        style={{ left: leftFromPercent(pct) }}
      >
        {pct}%
      </span>
    ))}
  </div>
</div>

                  <button
                    type="button"
                    onClick={() => handlePercentRangeRefine(facetKey)}
                    className="w-full flex items-center justify-center gap-[6px] py-[8px] mt-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer"
                  >
                    <img src={`${rmVars.themeUrl}images/search-ico-333.png`} className="w-[12px]" alt="search" />
                    Refine
                  </button>
                </div>
              );
            })}

            {/* jcr_jif_range는 가장 마지막에 렌더링 */}
            {facet?.jcr_jif_range && facet.jcr_jif_range.type === 'range' && (
              <div className="filter-item mb-9">
                <h4 className="mb-[18px] text-[14px] font-bold">
                  Jcr Jif Range
                </h4>

                <ul className="p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] flex text-[15px] gap-[5px] mb-[18px]">
                  <li
                    className={`${jifTab === 'range' ? 'active bg-white ' : ''}w-[calc((100%-5px)/2)] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer transition-colors duration-200`}
                    onClick={() => setJifTab('range')}
                  >
                    Range
                  </li>
                  <li
                    className={`${jifTab === 'individual' ? 'active bg-white ' : ''}w-[calc((100%-5px)/2)] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer transition-colors duration-200`}
                    onClick={() => setJifTab('individual')}
                  >
                    Individual
                  </li>
                </ul>

                {jifTab === 'range' ? (
                  <div>
                    <div className="relative w-full mx-auto mt-20">
                      <svg viewBox="0 0 400 80" className="absolute top-[-80px] left-0 w-full h-[80px] z-0">
                        <path
                          ref={jifSvgPathRef}
                          d={generateJifSVGPath}
                          fill="#4B4DFF1A"
                          stroke="none"
                        />
                      </svg>

                      <div ref={jifSliderRef} className="relative z-10"></div>

                      <div className="flex items-center gap-2 mt-6">
                        <input
                          ref={jifMinInputRef}
                          type="number"
                          step="0.1"
                          className="w-full h-[34px] px-2 border border-[#E4E8F1] rounded-lg text-[13px] text-[#333]"
                          placeholder={jifRangeFacet?.min?.toFixed(1)}
                        />
                        <span className="text-[#8D9AAF]">~</span>
                        <input
                          ref={jifMaxInputRef}
                          type="number"
                          step="0.1"
                          className="w-full h-[36px] px-2 border border-[#E4E8F1] rounded-lg text-[13px] text-[#333]"
                          placeholder={jifRangeFacet?.max?.toFixed(1)}
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleJifRangeRefine}
                      className="w-full flex items-center justify-center gap-[6px] py-[8px] mt-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer"
                    >
                      <img
                        src={`${rmVars.themeUrl}images/search-ico-333.png`}
                        className="w-[12px]"
                        alt="search"
                      />
                      Refine
                    </button>
                  </div>
                ) : (
                  <div>
                    <ul className="flex flex-col gap-1">
                      {jifRangeFacet?.array.slice(0, 5).map((item, idx) => {
                        const checked = Array.isArray(pendingFilters.jcr_jif_range) && pendingFilters.jcr_jif_range.includes(String(item.value));
                        return (
                          <li key={`${item.value}-${idx}`} className="w-full">
                            <label className="checkbox-wrap group inline-flex items-start cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="peer hidden"
                                checked={checked}
                                onChange={() => handleJifIndividualToggle(String(item.value))}
                              />
                              <span className="w-4 h-4 shrink-0 flex items-center justify-center mt-[3px] border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200" />
                              <span className="ml-[10px] text-[14px] text-[#6C7687]">
                                {item.value.toFixed(1)} (<span>{item.count.toLocaleString()}</span>)
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>

                    {jifRangeFacet && jifRangeFacet.array.length > 5 && (
                      <button
                        onClick={() => setPopup({ open: true, key: 'jcr_jif_range', title: 'JCR JIF Range' })}
                        className="btn-filter-more w-[calc(50%-8px)] flex items-center justify-center gap-[6px] py-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer lg:w-auto lg:py-[2px] lg:border-none lg:px-[10px] lg:bg-[#F3F5F9] lg:text-[13px] lg:text-[#8D9AAF] lg:gap-[2px] mt-4"
                      >
                        <img src={`${rmVars.themeUrl}images/more-ico-bk.png`} className="w-[14px] lg:hidden" alt="more" />
                        <img src={`${rmVars.themeUrl}images/more-ico-gr.png`} className="w-[14px] hidden lg:block" alt="more" />
                        More
                      </button>
                    )}

                    <button
                      onClick={handleJifIndividualRefine}
                      className="w-full flex items-center justify-center gap-[6px] py-[8px] mt-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer"
                    >
                      <img
                        src={`${rmVars.themeUrl}images/search-ico-333.png`}
                        className="w-[12px]"
                        alt="search"
                      />
                      Refine
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
